import { z } from 'zod';
import { HttpUrlSchema } from './hotels';

/**
 * The nine knowledge kinds (spec §5.1). `constraint` is the negative kind —
 * "nincs saját parkoló" — and goes into prompts as a prohibition, never as a
 * claim. New kinds can be added without a migration (record table), but the
 * reviewer UI groups by this list.
 */
export const HOTEL_FACT_KINDS = [
  'amenity',
  'room',
  'dining',
  'location',
  'audience',
  'season',
  'policy',
  'constraint',
  'usp',
] as const;
export const HotelFactKindSchema = z.enum(HOTEL_FACT_KINDS);
export type HotelFactKind = z.infer<typeof HotelFactKindSchema>;

export const HOTEL_FACT_STATUSES = ['suggested', 'approved', 'rejected', 'manual'] as const;
export const HotelFactStatusSchema = z.enum(HOTEL_FACT_STATUSES);
export type HotelFactStatus = z.infer<typeof HotelFactStatusSchema>;

/** The prompt-facing shape: what the copy/topic engines actually consume. */
export const KnowledgeFactSchema = z.object({
  kind: HotelFactKindSchema,
  label: z.string().min(1).max(120),
  value: z.string().min(1).max(600),
});
export type KnowledgeFact = z.infer<typeof KnowledgeFactSchema>;

/** One AI-proposed fact. The source quote is mandatory — see §6.4. */
export const AiFactSuggestionSchema = KnowledgeFactSchema.extend({
  sourceUrl: z.string().max(2048).nullable().default(null),
  sourceQuote: z.string().min(1).max(280),
  confidence: z.number().min(0).max(1).default(0.5),
});
export type AiFactSuggestion = z.infer<typeof AiFactSuggestionSchema>;

export const HotelFactSchema = KnowledgeFactSchema.extend({
  id: z.string().guid(),
  hotelId: z.string().guid(),
  sourceUrl: z.string().nullable(),
  sourceQuote: z.string().nullable(),
  status: HotelFactStatusSchema,
  confidence: z.number().nullable(),
  createdAt: z.string().datetime(),
  updatedAt: z.string().datetime(),
});
export type HotelFact = z.infer<typeof HotelFactSchema>;

/** Manager-authored fact (§8, "kézi tény hozzáadása"). Lands as `manual`. */
export const CreateHotelFactSchema = z.object({
  kind: HotelFactKindSchema,
  label: z.string().min(1).max(120),
  value: z.string().min(1).max(600),
  sourceUrl: HttpUrlSchema.nullable().optional(),
  sourceQuote: z.string().max(280).nullable().optional(),
});
export type CreateHotelFact = z.infer<typeof CreateHotelFactSchema>;

/**
 * Reviewer decision. `approved` locks the AI's value as-is; editing label/value
 * makes it `manual`; `rejected` keeps the row so the same value is not
 * re-proposed (§5.3).
 */
export const UpdateHotelFactSchema = z
  .object({
    label: z.string().min(1).max(120),
    value: z.string().min(1).max(600),
    status: z.enum(['approved', 'rejected', 'manual']),
  })
  .partial();
export type UpdateHotelFact = z.infer<typeof UpdateHotelFactSchema>;
